import { Blog } from "@/services/bedrock";

export const BLOG_BLOCK_TYPES: { type: Exclude<Blog.BlockType, "heading">; label: string }[] = [
  { type: "body", label: "Body" },
  { type: "list", label: "List items" },
  { type: "pullQuote", label: "Pull quote" },
  { type: "media", label: "Media" },
];

export const emptyBlogBlock = (type: Blog.BlockType): Blog.Block => {
  switch (type) {
    case "heading":
    case "pullQuote":
      return { type, text: "" };
    case "body":
      return { type, paragraphs: [] };
    case "list":
      return { type, items: [] };
    case "media":
      return { type, media: null, alt: "" };
  }
};

export const emptyBlogSection = (): Blog.Section => ({ blocks: [emptyBlogBlock("heading")] });

export const cleanTags = (tags: Array<string | unknown>): string[] =>
  tags.map(tag => String(tag).trim()).filter(Boolean);

export const parseTags = (value: string): string[] => cleanTags(value.split(","));

export const parseTagInput = (value: string | string[] | undefined): string[] =>
  Array.isArray(value) ? cleanTags(value) : parseTags(value ?? "");

export type ThemeOption = { id: number; label: string };

export const withCurrentThemeOption = (options: ThemeOption[], themeId: number | null | undefined): ThemeOption[] => {
  if (themeId == null || options.some(option => option.id === themeId)) return options;
  return [{ id: themeId, label: "Theme" }, ...options];
};

export const moveItem = <T>(items: T[], from: number, to: number): T[] => {
  if (from === to || from < 0 || to < 0 || from >= items.length || to >= items.length) return items;
  const next = [...items];
  const [moved] = next.splice(from, 1);
  next.splice(to, 0, moved);
  return next;
};

export const normalizeBlogBlock = (block: Blog.Block | Record<string, unknown>): Blog.Block => {
  const source = block as Blog.Block;
  const type = String((block as { type?: string }).type ?? "body") as Blog.BlockType;

  switch (type) {
    case "heading":
    case "pullQuote":
      return { type, text: source.text ?? "" };
    case "body":
      return { type, paragraphs: source.paragraphs ?? [] };
    case "list":
      return { type, items: source.items ?? [] };
    case "media":
      return { type, media: source.media ?? null, alt: source.alt ?? "" };
    default:
      return { type: "body", paragraphs: [] };
  }
};

export const ensureSectionHeading = (blocks: Blog.Block[]): Blog.Block[] => {
  const normalized = blocks.map(normalizeBlogBlock);
  const headings = normalized.filter(block => block.type === "heading");
  const rest = normalized.filter(block => block.type !== "heading");
  const heading = headings[0] ?? emptyBlogBlock("heading");
  const extras = headings
    .slice(1)
    .map(block => block.text?.trim())
    .filter((text): text is string => !!text);
  return extras.length > 0 ? [heading, ...rest, { type: "body", paragraphs: extras }] : [heading, ...rest];
};

export const normalizeBlogSection = (section: Blog.Section): Blog.Section => ({
  blocks: ensureSectionHeading(section.blocks ?? []),
});

export const normalizeBlogBody = (body: Blog.Body | null | undefined): Blog.Body => ({
  sections: (body?.sections ?? []).map(normalizeBlogSection),
  keyTakeaways: body?.keyTakeaways ?? [],
});

export const blogBlockText = (block?: Blog.Block): string => {
  if (!block) return "";
  switch (block.type) {
    case "heading":
    case "pullQuote":
      return block.text ?? "";
    case "body":
      return (block.paragraphs ?? []).join("\n\n");
    case "list":
      return (block.items ?? []).map(item => `• ${item}`).join("\n");
    case "media":
      return block.alt?.trim() || "Media";
    default:
      return "";
  }
};

export const blogSectionText = (section: Blog.Section): string =>
  (section.blocks ?? []).map(blogBlockText).filter(Boolean).join("\n\n");
